import { Component, DestroyRef, inject, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Router } from '@angular/router';
import { Alert } from "@models/alerts/alert";
import { AlertStatus } from "@models/alerts/alerts-enums";
import { EntityType } from '@models/common/entity-type.enum';
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { Company } from "@models/companies/company";
import { User } from "@models/organisations/user";
import { Trust } from '@models/trusts/trust';
import { TrustSubType, TrustType, trustTypeBySubtype } from '@models/trusts/trusts-enums';
import { NgbDropdown, NgbDropdownItem, NgbDropdownMenu, NgbDropdownToggle, NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  ManageTagsModalComponent
} from "@pages/settings/components/manage-tags-page/manage-tags-modal/manage-tags-modal.component";
import { TrustsExportService } from "@pages/trusts/services/trusts-export.service";
import { TrustsGridService } from "@pages/trusts/services/trusts-grid.service";
import { AlertsListComponent } from "@shared/components/alerts-list/alerts-list.component";
import { CommonPageComponent } from "@shared/components/commons/common-page/common-page.component";
import {
  PlatesActionsModalComponent
} from "@shared/components/commons/plates-actions-modal/plates-actions-modal.component";
import {
  AgTrustActionsComponent
} from '@shared/components/grid/ag-cells/trusts/ag-trust-actions/ag-trust-actions.component';
import {
  GridClientSideComponent
} from "@shared/components/grid/components/grid-client-side/grid-client-side.component";
import { NotesComponent } from '@shared/components/notes/notes.component';
import { ButtonComponent } from '@shared/controls/button/button.component';
import { HasRoleDirective } from "@shared/directives/has-role.directive";
import { IPartialHeaderData } from "@shared/services/header.service";
import { OrganisationApiService } from "@shared/services/organisation-api.service";
import { PageSizeService } from "@shared/services/page-size.service";
import { TrustsApiService } from "@shared/services/trusts-api.service";
import { doNothing } from "@shared/utils/doNothing";
import { getTrustProfilePath } from "@shared/utils/route-path-utils";
import { GridApi } from 'ag-grid-community';
import { finalize, Observable, of, take } from 'rxjs';
import { FormAddTrustComponent } from "../../../../forms/asic-forms/trusts";
import { trustEstablishmentActions } from "../../../../forms/asic-forms/trusts/constants/trust-establishment-texts";
import {
  TrustDiscretionaryEstablishment
} from "../../../../forms/models/trusts/trust-discretionary-establishment.model";
import { TrustEstablishmentBase } from "../../../../forms/models/trusts/trust-establishment-base.model";
import { TrustUnitEstablishment } from "../../../../forms/models/trusts/trust-unit-establishment.model";
import { ModalFormsService } from '../../../../forms/services/modal-forms.service';

@Component({
  selector: 'app-trusts',
  templateUrl: 'trusts-page.component.html',
  styleUrl: 'trusts-page.component.scss',
  standalone: true,
  imports: [
    ButtonComponent,
    NotesComponent,
    NgbDropdown,
    NgbDropdownToggle,
    NgbDropdownMenu,
    AlertsListComponent,
    HasRoleDirective,
    NgbDropdownItem,
    GridClientSideComponent
  ],
  providers: [
    TrustsGridService,
    TrustsExportService
  ]
})
export class TrustsPageComponent extends CommonPageComponent implements OnInit {
  private router = inject(Router);
  private trustsApiService = inject(TrustsApiService);
  private trustsExportService = inject(TrustsExportService);
  private trustsGridService = inject(TrustsGridService);
  private modalFormsService = inject(ModalFormsService);
  private modalService = inject(NgbModal);
  private organisationService = inject(OrganisationApiService);
  private destroyRef: DestroyRef = inject(DestroyRef);

  readonly pageTitle = 'Trusts';
  readonly EntityType = EntityType;
  readonly modalOpened = this.modalFormsService.modalOpened;
  override readonly headerData: IPartialHeaderData = {
    title: this.pageTitle,
    iconClass: 'ic-trusts-20'
  };

  trusts: Trust[] = [];
  checkedTrust: Trust[] = [];
  alerts: Alert[] = [];

  alertsVisible = false;
  entityForAlerts: Trust | undefined | null = null;

  notesVisible = false;
  entityForNotes: Trust | undefined | null = null;

  override ngOnInit() {
    super.ngOnInit();
    this.setColDefs();
    this.loadTrustList();
    this.trustsGridService.refreshFiltersTrigger$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.trustsGridService.setFiltersOptions(this.trusts);
      });
  }

  loadTrustList(): void {
    this.isLoading.set(true);
    this.trustsApiService.list()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe((trusts) => {
        const users = this.organisationService.getCachedProfileUsers();

        this.trusts = trusts.map((trust) => {
          users.forEach((user) => {
            if (user.id === trust.accountManagerId) {
              trust.accountManager = (user as unknown as User);
            }

            if (user.id === trust.partnerManagerId) {
              trust.partnerManager = (user as unknown as User);
            }
          });

          return trust;
        });

        this.trusts = trusts;
        this.trustsGridService.setFiltersOptions(this.trusts);
        this.setColDefs();
      });
  }

  addTrust(): void {
    if (this.modalOpened()) return;
    const modalRef = this.modalFormsService.openModal(FormAddTrustComponent);

    modalRef.result.then(
      (trust: Trust) => this.router.navigate(getTrustProfilePath(trust.entityId)),
      doNothing
    );
  }

  appointAccountManager(trust: Trust): void {
    console.log(trust);
  }

  managePins(trust: Trust): void {
    console.log(trust);
  }

  async openTrustProfile(trust: Trust): Promise<void> {
    await this.router.navigate(getTrustProfilePath(trust.entityId));
  }

  manageTags(): void {
    if (this.modalOpened()) return;
    const modalRef = this.modalService.open(ManageTagsModalComponent,
      {
        windowClass: 'manage-tags-modal',
        size: 'xl',
        centered: true
      });
    const instance = modalRef.componentInstance as ManageTagsModalComponent;

    instance.entities = this.checkedTrust;
    instance.confirm
      .pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.loadTrustList();
        this.selectTrust([]);
      });
  }


  openAlerts(trust: Trust): void {
    if (this.modalOpened()) return;
    this.alertsVisible = true;
    this.alerts = trust.alerts?.filter((alert) => {
      return alert.status !== AlertStatus.Archived && alert.status !== AlertStatus.Ignored;
    });
    this.entityForAlerts = trust;
  }

  onAlertReaded(alertId: string): void {
    if (!this.entityForAlerts) return;

    const relatedTrust = this.trusts.find(trustRecord => trustRecord.entityId == this.entityForAlerts?.entityId);
    const relatedAlertIndex = relatedTrust?.alerts.findIndex(alert => alert.id == alertId);

    if (relatedTrust && relatedAlertIndex != undefined && relatedAlertIndex > -1) {
      this.trustsGridService.gridApi.refreshCells({ force: true });
    }
  }

  openNotes(trust: Trust): void {
    if (this.modalOpened()) return;
    this.notesVisible = true;
    this.entityForNotes = trust;
  }

  openSelectTrustEstablishmentModal(): void {
    const modalRef = this.modalService.open(PlatesActionsModalComponent);
    const instance = modalRef.componentInstance as PlatesActionsModalComponent;

    instance.header = 'Trust Establishment';
    instance.iconClass = 'ic-trusts-20';
    instance.actionGroups = [
      {
        title: 'Discretionary Trust',
        actions: [
          {
            title: trustEstablishmentActions.discretionaryTrust.title,
            description: trustEstablishmentActions.discretionaryTrust.description,
            action: () => this.openTrustEstablishmentForm(TrustSubType.Discretionary),
          },
          {
            title: trustEstablishmentActions.discretionaryTrustWithFbe.title,
            description: trustEstablishmentActions.discretionaryTrustWithFbe.description,
            action: () => this.openTrustEstablishmentForm(TrustSubType.DiscretionaryWithFBE),
          },
          {
            title: trustEstablishmentActions.hybridDiscretionaryTrust.title,
            description: trustEstablishmentActions.hybridDiscretionaryTrust.description,
            action: () => this.openTrustEstablishmentForm(TrustSubType.DiscretionaryCapitalReserved),
            disabled: true,
          },
          {
            title: trustEstablishmentActions.hybridDiscretionaryTrustWithForeignBeneficiaryExclusionFBE.title,
            description: trustEstablishmentActions.hybridDiscretionaryTrustWithForeignBeneficiaryExclusionFBE.description,
            action: () => this.openTrustEstablishmentForm(TrustSubType.DiscretionaryFullyReserved),
            disabled: true,
          },
        ]
      },
      {
        title: 'Unit Trusts',
        actions: [
          {
            title: trustEstablishmentActions.standardUnitTrust.title,
            description: trustEstablishmentActions.standardUnitTrust.description,
            action: () => this.openTrustEstablishmentForm(TrustSubType.NonFixed),
          },
          {
            title: trustEstablishmentActions.fixedUnitTrustForIncomeTaxPurposes.title,
            description: trustEstablishmentActions.fixedUnitTrustForIncomeTaxPurposes.description,
            action: () => this.openTrustEstablishmentForm(TrustSubType.Fixed),
            disabled: true,
          },
          // {
          //   title: trustEstablishmentActions.SISRegUnitTrust.title,
          //   description: trustEstablishmentActions.SISRegUnitTrust.description,
          //   action: () => of(true),
          //   disabled: true,
          // },
          // {
          //   title: trustEstablishmentActions.nonControlledUnitTrust.title,
          //   description: trustEstablishmentActions.nonControlledUnitTrust.description,
          //   action: () => of(true),
          //   disabled: true,
          // },
          {
            title: trustEstablishmentActions.NSWLandTaxUnitTrust.title,
            description: trustEstablishmentActions.NSWLandTaxUnitTrust.description,
            action: () => this.openTrustEstablishmentForm(TrustSubType.NSWLandTax),
            disabled: true,
          },
        ]
      }
    ];
  }

  openTrustEstablishmentForm(subType: TrustSubType): Observable<boolean> {
    const change = this.getTrustPayload(subType);
    this.modalFormsService.openTrustModalWithCompany(change, new Company());

    return of(true);
  }

  getTrustPayload(subType: TrustSubType): TrustEstablishmentBase {
    const trustType = trustTypeBySubtype[subType];

    if (trustType == TrustType.Discretionary) {
      return new TrustDiscretionaryEstablishment({ subType, trustType });
    } else if (trustType == TrustType.Unit) {
      return new TrustUnitEstablishment({ subType, trustType });
    }

    throw new Error('Unknown trust type');
  }

  // =====  Grid  =====

  readonly pinFirstColumnName = 'name';
  readonly defaultPageSize = PageSizeService.defaultPageSize;

  colDefs = this.trustsGridService.colDefs;

  onGridReady(gridApi: GridApi): void {
    this.trustsGridService.setGridApi(gridApi);
  }

  selectTrust(trusts: Trust[]): void {
    this.checkedTrust = trusts;
  }

  setColDefs(): void {
    this.trustsGridService.setColDefs({
      field: '',
      width: 150,
      sortable: false,
      cellClass: 'actions-button-cell',
      cellRenderer: AgTrustActionsComponent,
      cellRendererParams: {
        openTrustProfile: this.openTrustProfile.bind(this),
        appointAccountManager: this.appointAccountManager.bind(this),
        exportTrustProfile: this.exportTrustProfile.bind(this),
        managePins: this.managePins.bind(this),
        openAlerts: this.openAlerts.bind(this),
        openNotes: this.openNotes.bind(this),
      },
      suppressHeaderMenuButton: true,
    });
  }

  // =====  Export  =====

  readonly excelStyles = this.trustsExportService.excelStyles;
  readonly ExportTypeEnum = ExportTypeEnum;

  excelExportParams = this.trustsExportService.exportParamsXls();
  csvExportParams = this.trustsExportService.exportParamsCsv();

  exportSelectedTrustsToXls(): void {
    this.exportTrustList(true, ExportTypeEnum.EXCEL);
  }

  exportSelectedTrustsToCsv(): void {
    this.exportTrustList(true, ExportTypeEnum.CSV);
  }

  exportTrustProfile(trust: Trust): void {
    this.trustsExportService.exportTrustProfile(
      this.trustsGridService.gridApi,
      trust,
      this.colDefs()
    );
  }

  exportTrustList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    if (this.modalOpened()) return;
    this.trustsExportService.exportTrustList(
      this.trustsGridService.gridApi,
      this.colDefs(),
      isBulkExport,
      exportType,
      this.checkedTrust,
      this.trusts,
    );
  }
}
